import React from "react";
import { TAdhocTaskForms, TCasesDayViewForm, TCasesCaseTaskFormForDayView, TCasesCaseTaskFormAdhocForDayView } from "../../../../../../domain/codecs/form/CasesForm";
import { TCasesDayViewRoadblocksForm, TRoadblockDayViewListItem } from "../../../../../../domain/codecs/form/RoadblockDayViewForm";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { CRMColors } from "../../../../models/CRMColors";
import { AnimationPopout } from "../../../BuildingBlocks/AnimationPopout";
import { Background } from "../../../BuildingBlocks/Background";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { CRMThinColoredButton } from "../../../Simple/CRMThinColoredButton/CRMThinColoredButton";
import { WeightSemiBold } from "../../../WeightSemiBold/WeightSemiBold";
import { CRMEmptyPlaceholder } from "../../../Simple/CRMEmptyPlaceholder/CRMEmptyPlaceholder";
import { CRMFontSizes } from "../../../../models/CRMFontSizes";
import { CRMDayTablePlaceholderRows } from "../CRMDayTablePlaceholderRows/CRMDayTablePlaceholderRows";
import { CRMCasesTaskRowChangeable } from "../CRMCasesTaskRowChangeable/CRMCasesTaskRowChangeable";
import { TPlainUser } from "../../../../../../domain/codecs/User";
import { CRMCasesTaskRow } from "../CRMCasesTaskRow/CRMCasesTaskRow";
import { CRMDayRoadblockRow } from "../CRMDayRoadblockRow/CRMDayRoadblockRow";
import { CRMDayPriorityBand, dayPriorityBandOf, TDayPriorityBand } from "../CRMDayPriorityBand/CRMDayPriorityBand";
import { CRMDayParkedBand } from "../CRMDayParkedBand/CRMDayParkedBand";
import { TDaySortKey } from "../../../../../../domain/codecs/DaySortKey";

type TCRMDayCombinedTableProps = {
    dayForm: TCasesDayViewForm;
    roadblocksForm: TCasesDayViewRoadblocksForm;
    sort: TDaySortKey;
    assignableUsers: Array<TPlainUser>;
    isDateShown: boolean;
    isDateLeavingTheList: (date: string | null) => boolean;
    isAssigneeLeavingTheList: (assignedToUserId: string) => boolean;
    onLoadMore: () => void;
    onChangeTask: (form: TAdhocTaskForms) => void;
    onArchiveTask: (form: TAdhocTaskForms) => void;
    onChangeRoadblockAssignee: (payload: { roadblock_id: string, assigned_to_user_id: string }) => void;
    onAwaitRoadblock: (payload: { roadblock_id: string, awaitable_entities_id: string, days_override: number | null }) => void;
    onCancelAwaitRoadblock: (payload: { roadblock_id: string }) => void;
    getAheadDay: { isoDate: string, label: string, count: number } | null;
    onGoToDate: (isoDate: string) => void;
};

type TWorkRow =
    | { source: "task", sortRank: number, id: string, caseId: string, band: TDayPriorityBand, form: TCasesCaseTaskFormForDayView }
    | { source: "roadblock", sortRank: number, id: string, caseId: string, band: TDayPriorityBand, roadblock: TRoadblockDayViewListItem };

const isAdhocTaskForm = (form: TCasesCaseTaskFormForDayView): form is TCasesCaseTaskFormAdhocForDayView =>
    form.children.category === "task";
const isKeyDateTaskForm = (form: TCasesCaseTaskFormForDayView): form is Exclude<TCasesCaseTaskFormForDayView, TCasesCaseTaskFormAdhocForDayView> =>
    form.children.category === "key_date";

const sortRows = (a: TWorkRow, b: TWorkRow): number => a.sortRank - b.sortRank;

export const CRMDayCombinedTable = (props: TCRMDayCombinedTableProps): JSX.Element => {
    const isLoading =
        props.dayForm.status === "submitting"
        || props.roadblocksForm.status === "submitting";

    const taskRows: Array<TWorkRow> = props.dayForm.children.task_forms.map((form) => ({
        source: "task" as const,
        sortRank: form.children.sort_rank,
        id: `${form.original.id}_${form.original.tag}`,
        caseId: form.children.case_id,
        band: dayPriorityBandOf({
            isSignoff: false,
            isUnderOffer: form.children.is_under_offer,
        }),
        form,
    }));

    const roadblockRows: Array<TWorkRow> = props.roadblocksForm.children.roadblocks.map((roadblock) => ({
        source: "roadblock" as const,
        sortRank: roadblock.sort_rank,
        id: roadblock.id,
        caseId: roadblock.case_id,
        band: dayPriorityBandOf({
            isSignoff: roadblock.status === "awaiting_signoff",
            isUnderOffer: roadblock.is_under_offer,
        }),
        roadblock,
    }));

    const rows = [...taskRows, ...roadblockRows].sort(sortRows);

    const isBandShown = props.sort === "priority";

    const isCaseContinuation = (index: number): boolean =>
        index > 0
        && rows[index - 1].caseId === rows[index].caseId
        && (!isBandShown || rows[index - 1].band === rows[index].band);

    const isShowingLoadMore = props.dayForm.children.available_count > rows.length;

    const isNoRows = rows.length === 0 && !isLoading;

    const parkedRoadblocks = props.roadblocksForm.children.parked_roadblocks;
    const hasParked = parkedRoadblocks.length > 0;

    const parkedBand = (
        <CRMDayParkedBand
            parkedRoadblocks={parkedRoadblocks}
            assignableUsers={props.assignableUsers}
            awaitableEntities={props.roadblocksForm.children.awaitable_entities}
            isDayClear={isNoRows}
            onChangeAssignee={props.onChangeRoadblockAssignee}
            onAwait={props.onAwaitRoadblock}
            onCancelAwait={props.onCancelAwaitRoadblock}
        />
    );

    return (
        <>
            {!isLoading && (
                <Background padding={`0px ${CRMSpacing.X_LARGE}`}>
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <SpacingColumn>
                            {rows.map((row, index) => (
                                <React.Fragment key={row.id}>
                                    {isBandShown && (index === 0 || rows[index - 1].band !== row.band) &&
                                        <CRMDayPriorityBand band={row.band} />
                                    }

                                    {row.source === "roadblock"
                                        ? <CRMDayRoadblockRow
                                            roadblock={row.roadblock}
                                            assignableUsers={props.assignableUsers}
                                            awaitableEntities={props.roadblocksForm.children.awaitable_entities}
                                            isDateShown={props.isDateShown}
                                            isCaseContinuation={isCaseContinuation(index)}
                                            onChangeAssignee={props.onChangeRoadblockAssignee}
                                            onAwait={props.onAwaitRoadblock}
                                            onCancelAwait={props.onCancelAwaitRoadblock}
                                        />
                                        : isAdhocTaskForm(row.form)
                                            ? <CRMCasesTaskRowChangeable
                                                form={row.form}
                                                assignableUsers={props.assignableUsers}
                                                isDateShown={props.isDateShown}
                                                isCaseContinuation={isCaseContinuation(index)}
                                                isDateLeavingTheList={props.isDateLeavingTheList}
                                                isAssigneeLeavingTheList={props.isAssigneeLeavingTheList}
                                                onChange={props.onChangeTask}
                                                onArchive={props.onArchiveTask}
                                            />
                                            : isKeyDateTaskForm(row.form)
                                                ? <CRMCasesTaskRow
                                                    form={row.form}
                                                    isDateShown={props.isDateShown}
                                                    isCaseContinuation={isCaseContinuation(index)}
                                                />
                                                : <></>
                                    }
                                </React.Fragment>
                            ))}
                        </SpacingColumn>

                        {isShowingLoadMore && (
                            <AnimationPopout>
                                <Background padding={`0px 0px ${CRMSpacing.LARGE} 0px`}>
                                    <SpacingRow justifyContent="center">
                                        <CRMThinColoredButton
                                            backgroundColor={CRMColors.NEUTRAL_2}
                                            fontColor={CRMColors.NEUTRAL_12}
                                            label={<WeightSemiBold>See more</WeightSemiBold>}
                                            onClick={props.onLoadMore}
                                            icon="down-arrow"
                                        />
                                    </SpacingRow>
                                </Background>
                            </AnimationPopout>
                        )}

                        {/* When the day is clear the band moves into the empty state
                            below, which frames it — rendering it here as well would
                            show the same rows twice. */}
                        {!isNoRows && parkedBand}
                    </SpacingColumn>
                </Background>
            )}

            {isLoading && <CRMDayTablePlaceholderRows />}

            {isNoRows && hasParked && (
                <Background padding={`50px ${CRMSpacing.X_LARGE} 0px ${CRMSpacing.X_LARGE}`}>
                    <SpacingColumn spacing={CRMSpacing.LARGE}>
                        <CRMEmptyPlaceholder
                            iconSize="60px"
                            fontSize={CRMFontSizes.LARGE}
                        >
                            Nothing left for today. Here's what you've parked.
                        </CRMEmptyPlaceholder>

                        {parkedBand}
                    </SpacingColumn>
                </Background>
            )}

            {isNoRows && !hasParked && (
                <Background padding="50px 0px 0px 0px">
                    <SpacingColumn spacing={CRMSpacing.LARGE}>
                        <CRMEmptyPlaceholder
                            iconSize="100px"
                            fontSize={CRMFontSizes.X_LARGE}
                        >
                            {props.getAheadDay === null
                                ? "Nothing to see here..."
                                : "All clear here. Fancy getting ahead?"}
                        </CRMEmptyPlaceholder>
                        {props.getAheadDay !== null &&
                            <SpacingRow justifyContent="center">
                                <CRMThinColoredButton
                                    backgroundColor={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2}
                                    fontColor={CRMColors.NEUTRAL_PAPER}
                                    label={<WeightSemiBold>
                                        {`Get ahead — ${props.getAheadDay.count} lined up for ${props.getAheadDay.label}`}
                                    </WeightSemiBold>}
                                    onClick={() => props.onGoToDate(props.getAheadDay !== null ? props.getAheadDay.isoDate : "")}
                                />
                            </SpacingRow>
                        }
                    </SpacingColumn>
                </Background>
            )}
        </>
    );
};
